
import React, { useState } from 'react';
import { useSleepSchedule, ScheduleItem } from './SleepScheduleContext';
import { Check, AlertCircle, Minus, Clock, Sun, Moon, HelpCircle, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface SleepScheduleTimelineProps {
  date: Date;
}

const SleepScheduleTimeline: React.FC<SleepScheduleTimelineProps> = ({ date }) => {
  const { scheduleItems, isSpecialist } = useSleepSchedule();
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  
  const formatTime = (timeString: string) => {
    if (!timeString) return '';
    
    const date = new Date(timeString);
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'pm' : 'am';
    
    hours = hours % 12;
    hours = hours ? hours : 12; // Convert 0 to 12
    
    return `${hours}:${minutes} ${ampm}`;
  };

  const formatDuration = (startTime: string, endTime: string) => {
    if (!startTime || !endTime) return '';
    
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.round(diffMs / (1000 * 60));
    
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;
    
    if (hours === 0) return `${minutes}min`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}min`;
  };

  const formatPlannedTime = (item: ScheduleItem) => {
    const startTime = formatTime(item.scheduledStartTime);
    const isAwake = item.type === 'wake';
    
    // For wake windows, show as a range with duration if end time exists
    if (item.type === 'wake' && item.scheduledEndTime) {
      const endTime = formatTime(item.scheduledEndTime);
      const duration = formatDuration(item.scheduledStartTime, item.scheduledEndTime);
      return (
        <div className="flex flex-col gap-0.5">
          <span className="bg-amber-100 text-amber-900 px-2.5 py-0.5 rounded-md text-sm font-semibold border border-amber-300">
            {startTime} - {endTime}
          </span>
          <span className="text-xs text-amber-700 font-bold">({duration})</span>
        </div>
      );
    }
    
    // For naps and bedtime, show single time
    return (
      <span className="bg-blue-100 text-blue-900 px-2.5 py-0.5 rounded-md text-sm font-semibold border border-blue-300">
        {startTime}
      </span>
    );
  };
  
  const getStatusIcon = (status: ScheduleItem['status']) => {
    switch (status) {
      case 'on-time':
        return <Check className="h-5 w-5 text-green-500" />;
      case 'late':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'short':
        return <Minus className="h-5 w-5 text-orange-400" />;
      case 'missed':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'off-schedule':
        return <Clock className="h-5 w-5 text-purple-500" />;
      case 'not-logged':
        return <X className="h-5 w-5 text-gray-400" />;
      default:
        return null;
    }
  };

  const getVarianceText = (item: ScheduleItem) => {
    if (!item.actualStartTime) return null;
    
    const planned = new Date(item.scheduledStartTime);
    const actual = new Date(item.actualStartTime);
    const diffMinutes = Math.round((actual.getTime() - planned.getTime()) / (1000 * 60));
    
    if (Math.abs(diffMinutes) <= 5) return null; // Within 5 minutes - no need to show
    
    if (diffMinutes > 0) {
      return <span className="text-xs text-yellow-600">+{diffMinutes}min late</span>;
    } else {
      return <span className="text-xs text-blue-600">{diffMinutes}min early</span>;
    }
  };
  
  const getTypeIcon = (type: ScheduleItem['type']) => {
    switch (type) {
      case 'wake':
        return <Sun className="h-4 w-4 text-amber-500 mr-2" />;
      case 'nap':
        return <Moon className="h-4 w-4 text-blue-500 rotate-180 mr-2" />;
      case 'bedtime':
        return <Moon className="h-4 w-4 text-indigo-500 mr-2" />;
      default:
        return null;
    }
  };
  
  // Check if adaptive scheduling is active (any actuals differ significantly from planned)
  const hasAdaptiveScheduling = scheduleItems.some((item, index) => {
    if (!item.actualStartTime) return false;
    const actualTime = new Date(item.actualStartTime).getTime();
    const plannedTime = new Date(item.scheduledStartTime).getTime();
    const diffMinutes = Math.abs(actualTime - plannedTime) / (1000 * 60);
    return diffMinutes > 15;
  });
  
  return (
    <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
      {scheduleItems.length === 0 && (
        <div className="bg-gray-50 p-6 rounded-lg text-center">
          {isSpecialist ? (
            <p className="text-gray-500">No schedule has been created yet. Click the "Create Schedule" button to get started.</p>
          ) : (
            <p className="text-gray-500">No sleep schedule has been assigned yet.</p>
          )}
        </div>
      )}
      
      {scheduleItems.length > 0 && (
        <>
          {/* Adaptive Scheduling Alert - shown when schedule has been adapted */}
          {hasAdaptiveScheduling && (
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-100 px-6 py-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <p className="text-sm text-blue-800">
                  <strong>Smart Adaptation Active:</strong> Future times have been automatically adjusted based on your actual logged times to maintain proper wake window spacing.
                </p>
              </div>
            </div>
          )}
          
          {/* Info Banner */}
          <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-indigo-100 px-6 py-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  <HelpCircle className="h-4 w-4 text-indigo-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-indigo-900 mb-1">AI-Generated Sleep Schedule</h4>
                <p className="text-sm text-indigo-700 leading-relaxed">
                  This personalized schedule is created using age-appropriate sleep science, your child's recent patterns, and evidence-based wake window guidelines.
                </p>
              </div>
              <Button
                onClick={() => setIsInfoOpen(true)}
                className="flex-shrink-0 px-3 py-1.5 text-sm font-medium text-indigo-700 bg-white hover:bg-indigo-50 border border-indigo-200 rounded-lg transition-colors"
                variant="ghost"
              >
                Learn More
              </Button>
            </div>
          </div>
          
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Planned</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actual</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {scheduleItems.map((item, index) => (
                <tr key={item.id} className={`
                  ${item.type === 'wake' 
                    ? 'bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100' 
                    : 'bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100'
                  }
                `}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className={`
                        w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0
                        ${item.type === 'wake' 
                          ? 'bg-amber-500 shadow-lg shadow-amber-200' 
                          : 'bg-blue-500 shadow-lg shadow-blue-200'
                        }
                      `}>
                        {item.type === 'wake' ? (
                          <Sun className="h-5 w-5 text-white" />
                        ) : (
                          <Moon className="h-5 w-5 text-white" />
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className={`
                          text-xs font-bold uppercase tracking-wide mb-0.5
                          ${item.type === 'wake' ? 'text-amber-700' : 'text-blue-700'}
                        `}>
                          {item.type === 'wake' ? 'AWAKE' : 'SLEEP'}
                        </span>
                        <span className="font-medium text-gray-900">{item.label}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {formatPlannedTime(item)}
                    {/* Show adaptive scheduling indicator if times were adjusted */}
                    {(() => {
                      const now = new Date();
                      const planned = new Date(item.scheduledStartTime);
                      // Check if this is a future item that might have been adjusted
                      if (now.getTime() < planned.getTime() && index > 0) {
                        // Check if any previous items have actuals that differ significantly
                        const hasPreviousActuals = scheduleItems.slice(0, index).some(prev => {
                          if (!prev.actualStartTime) return false;
                          const actualTime = new Date(prev.actualStartTime).getTime();
                          const plannedTime = new Date(prev.scheduledStartTime).getTime();
                          const diffMinutes = Math.abs(actualTime - plannedTime) / (1000 * 60);
                          return diffMinutes > 15;
                        });
                        
                        if (hasPreviousActuals) {
                          return (
                            <div className="flex items-center gap-1 mt-1">
                              <Clock className="h-3 w-3 text-blue-500" />
                              <span className="text-xs text-blue-600 italic">Adapted</span>
                            </div>
                          );
                        }
                      }
                      return null;
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {item.actualStartTime ? (
                      <div className="flex flex-col gap-1">
                        <span className={`px-2.5 py-0.5 rounded-md text-sm ${
                          item.status === 'on-time' ? 'bg-green-100 text-green-800' :
                          item.status === 'late' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {formatTime(item.actualStartTime)}
                        </span>
                        {getVarianceText(item)}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-gray-700">{item.duration || '-'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-gray-500">{item.targetDuration || '-'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center">
                        {getStatusIcon(item.status)}
                        <span className="ml-2 text-sm text-gray-600">
                          {item.status === 'on-time' ? 'On time' : 
                           item.status === 'late' ? 'Late' :
                           item.status === 'short' ? 'Short' : 
                           item.status === 'missed' ? 'Missed' :
                           item.status === 'off-schedule' ? 'Off Schedule' : 
                           'Not Logged'}
                        </span>
                      </div>
                      {/* Show closest log info for off-schedule items */}
                      {item.status === 'off-schedule' && item.meta?.closestOutsideThreshold && (
                        <span className="text-xs text-purple-600 italic">
                          Closest: {item.meta.closestOutsideThreshold.diffMinutes > 0 ? '+' : ''}{item.meta.closestOutsideThreshold.diffMinutes}min
                        </span>
                      )}
                      {/* Show explanation for not-logged items */}
                      {item.status === 'not-logged' && (
                        <span className="text-xs text-gray-500 italic">
                          No logs found
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      
      {/* Notes and Learn More section at the bottom */}
      {scheduleItems.length > 0 && (
        <div className="border-t border-gray-200 bg-gray-50">
          <div className="p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
            <p className="text-sm text-gray-600">
              The National Sleep Foundation suggests that little ones this age should get around 13.5hrs of total sleep per day. Aim for 11-12 hours at night.
            </p>
          </div>
          
          <div className="p-4 bg-primary/5 border-t border-primary/20">
            <h4 className="font-medium mb-3 flex items-center gap-2 text-sm">
              <HelpCircle className="h-4 w-4 text-primary" />
              Learn More: Evidence-Based Sources
            </h4>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div>
                <p className="font-medium text-foreground mb-1">American Academy of Sleep Medicine (AASM)</p>
                <p>The AASM provides clinical practice guidelines for optimal sleep durations across age groups. Their research forms the foundation of pediatric sleep recommendations used by healthcare professionals worldwide.</p>
              </div>
              <div>
                <p className="font-medium text-foreground mb-1">National Sleep Foundation</p>
                <p>The NSF conducts extensive research on sleep patterns and publishes consensus recommendations for sleep duration and wake windows based on developmental stages. Their guidelines are regularly updated based on the latest scientific evidence.</p>
              </div>
              <div className="pt-2 border-t border-border">
                <p className="text-xs italic">
                  Our AI personalizes these evidence-based guidelines by analyzing your child's recent sleep patterns, age-specific needs, and individual variations to provide tailored recommendations while staying within scientifically validated ranges.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Info Dialog */}
      <Dialog open={isInfoOpen} onOpenChange={setIsInfoOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">How We Generate Sleep Schedules</DialogTitle>
            <DialogDescription className="text-base">
              Our AI analyzes multiple data sources to create personalized recommendations
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 mt-4">
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 border border-indigo-100 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center font-semibold flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Age-Appropriate Guidelines</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    We reference evidence-based sleep metrics from pediatric sleep research, including expected total sleep duration, number of naps, and wake window ranges for your child's specific age. These guidelines are based on recommendations from organizations like the National Sleep Foundation and American Academy of Pediatrics.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-100 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-semibold flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Recent Sleep Patterns</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    The AI reviews your child's sleep and nap sessions from the past 7 days to understand their natural rhythms and preferences. This includes analyzing sleep onset times, duration patterns, and wake-up consistency to align recommendations with your child's unique biology.
                  </p>
                </div>
              </div>
            </div>
            
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-600 text-white flex items-center justify-center font-semibold flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Wake Window Science</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-2">
                    Wake windows are the periods when your baby is awake between sleep sessions. We use age-specific guidelines to determine optimal awake time durations that prevent overtiredness while ensuring adequate wake time for development.
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    The schedule shows wake periods as <strong>time ranges with durations</strong> (e.g., "11:30 am - 3:00 pm (3h 30min)") to clearly indicate when your baby should be awake and for how long.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-100 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold flex-shrink-0">
                  4
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Adaptive Scheduling</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-2">
                    Life happens! When actual times differ from the plan, our system <strong>automatically adjusts future schedule items</strong> for the same day to maintain appropriate wake window durations.
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    For example, if nap 1 starts 30 minutes late, all subsequent items (wake window 2, nap 2, etc.) are shifted by 30 minutes to preserve the intended spacing. Items marked with <Clock className="h-3 w-3 text-blue-500 inline" /> <span className="text-xs text-blue-600 italic">Adapted</span> have been automatically adjusted.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="border-t-2 border-gray-200 pt-5 mt-2">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-600" />
                Understanding Wake Windows
              </h3>
              <div className="space-y-3 pl-7">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="font-medium text-gray-800 text-sm mb-1">What is a Wake Window?</p>
                  <p className="text-sm text-gray-600">
                    A wake window is the period of time your baby is awake, starting from when they wake up until they go down for the next sleep period (nap or bedtime).
                  </p>
                </div>
                <div>
                  <p className="font-medium text-gray-800 text-sm">Minimum Duration</p>
                  <p className="text-sm text-gray-600">The earliest time your baby might be ready for sleep. Putting them down before this may result in resistance or difficulty falling asleep.</p>
                </div>
                <div>
                  <p className="font-medium text-gray-800 text-sm">Maximum Duration</p>
                  <p className="text-sm text-gray-600">The longest they should stay awake before becoming overtired. Going past this threshold often leads to crankiness, shorter naps, and more night wakings.</p>
                </div>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 mt-4">
                  <p className="text-sm text-indigo-900 italic">
                    💡 <strong>Pro Tip:</strong> Staying within the recommended wake window ranges helps establish healthy sleep patterns, improves sleep quality, and supports your child's overall development and well-being.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SleepScheduleTimeline;
